// ./src/components/features/Accommodations/AccommodationsFilterBar/AccommodationsFilterBar.tsx
//
// Filter controls for the event Accommodations list: by status (active / disabled)
// and by lodging category (from the shared "accommodation" vocabulary). Fully
// controlled — the page owns the value and maps it to an AccommodationFilter.
// Name/address search lives in the page header, not here.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, CategorySelect } from "@/components/generic";

export type AccommodationStatusFilter = "active" | "disabled";

export interface AccommodationsFilterValue {
  status: AccommodationStatusFilter | null;
  categoryId: string | null;
}

export interface AccommodationsFilterBarProps {
  value: AccommodationsFilterValue;
  onChange: (value: AccommodationsFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_ACCOMMODATIONS_FILTER: AccommodationsFilterValue = {
  status: null,
  categoryId: null,
};

export const accommodationsFilterActiveCount = (value: AccommodationsFilterValue): number =>
  [value.status, value.categoryId].filter(Boolean).length;

const isFilterActive = (value: AccommodationsFilterValue): boolean =>
  accommodationsFilterActiveCount(value) > 0;

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };
const CATEGORY_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

const ANY = "any";

const AccommodationsFilterBarComponent: React.FC<AccommodationsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const statusOptions = [
    { value: ANY, label: t("events:accommodations.filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("events:accommodations.filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("events:accommodations.filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("events:accommodations.filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as AccommodationStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={CATEGORY_STYLE}>
        <CategorySelect
          group="accommodation"
          exactGroup
          label={t("events:accommodations.filters.category.label", { defaultValue: "Type" })}
          placeholder={t("events:accommodations.filters.category.placeholder", { defaultValue: "All types" })}
          value={value.categoryId}
          onChange={(categoryId) => onChange({ ...value, categoryId })}
          disabled={disabled}
          clearable
          size="md"
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_ACCOMMODATIONS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const AccommodationsFilterBar = React.memo(AccommodationsFilterBarComponent);
